import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, pollRefresh } from '../lib/api';
import { GlassCard } from '../components/GlassCard';
import {
  ArrowLeft, Briefcase, Coins, TrendingUp, Package, ShieldAlert, LineChart,
  Zap, Truck, Sparkles, RefreshCcw, Lock, AlertTriangle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PurchaseNegotiatorSections from '../components/PurchaseNegotiatorSections';

const META = {
  ceo:       { title: 'AI CEO',       sub: 'Business Overview',     grad: ['#7C5CFF','#5B3FE5'], Icon: Briefcase },
  cfo:       { title: 'AI CFO',       sub: 'Cash & GST',            grad: ['#F59E0B','#B45309'], Icon: Coins },
  sales:     { title: 'AI Sales',     sub: 'Pipeline',              grad: ['#22D3EE','#0EA5B7'], Icon: TrendingUp },
  inventory: { title: 'AI Inventory', sub: 'Stock Health',          grad: ['#6366F1','#4F46E5'], Icon: Package },
  fraud:     { title: 'AI Fraud',     sub: 'Anomaly Detection',     grad: ['#EF4444','#B91C1C'], Icon: ShieldAlert },
  market:    { title: 'AI Market',    sub: 'Price Intelligence',    grad: ['#F97316','#C2410C'], Icon: LineChart },
  reactive:  { title: 'AI Reactive',  sub: 'Live Triggers · 24h',   grad: ['#EAB308','#A16207'], Icon: Zap },
  purchase:  { title: 'AI Purchase',  sub: 'Supplier Intelligence', grad: ['#10B981','#047857'], Icon: Truck },
  proactive: { title: 'AI Proactive', sub: 'Business Signals',     grad: ['#EC4899','#9F1239'], Icon: Sparkles },
};

function inr(n) {
  if (!n || n === 0) return '₹0';
  try { return '₹' + Math.round(n).toLocaleString('en-IN'); } catch { return '₹' + String(Math.round(n)); }
}

const SEV_STYLES = {
  info:     { bg: 'bg-emerald-500/15', text: 'text-emerald-700' },
  warn:     { bg: 'bg-yellow-500/15', text: 'text-yellow-700' },
  critical: { bg: 'bg-red-500/15', text: 'text-red-700' },
  high:     { bg: 'bg-red-500/15', text: 'text-red-700' },
  med:      { bg: 'bg-yellow-500/15', text: 'text-yellow-700' },
  low:      { bg: 'bg-emerald-500/15', text: 'text-emerald-700' },
};

export default function ManagerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { token, hasPlan } = useAuth();
  const isPremium = hasPlan('premium');
  const s = slug in META ? slug : 'ceo';
  const meta = META[s];
  const isLlmSlug = ['reactive', 'purchase', 'proactive'].includes(s);

  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const analysisGenRef = useRef(null);
  const [sheet, setSheet] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [approved, setApproved] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.businessHealth(token).then(setHealth).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const loadAnalysis = useCallback(async (forceRefresh = false) => {
    if (!token || !isLlmSlug) return;
    setAnalysisLoading(true);
    try {
      let data;
      if (forceRefresh) {
        const prevGenAt = analysisGenRef.current;
        data = await pollRefresh({
          postFn: () => api.managerRefresh(token, s),
          getFn: () => api.managerAnalysis(token, s, false),
          prevGeneratedAt: prevGenAt,
          pollIntervalMs: 5000,
          maxWaitMs: 120000,
        });
      } else {
        data = await api.managerAnalysis(token, s, false);
      }
      console.log('[ManagerDetail] data loaded', { generated_at: data?.generated_at, source: data?.source, insights: data?.insights?.length });
      // Full state replacement — not a merge
      setAnalysis(data);
      analysisGenRef.current = data?.generated_at || null;
      if (forceRefresh) {
        const toastMsg = data?.degraded ? 'Refreshed (using fallback)' : 'Analysis refreshed';
        setToast({ msg: toastMsg, type: 'success' });
        setTimeout(() => setToast(null), 5000);
      }
    } catch (e) {
      setToast({ msg: e?.message || 'Failed to load analysis', type: 'error' });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setAnalysisLoading(false);
    }
  }, [token, isLlmSlug, s]);

  useEffect(() => { if (isLlmSlug) loadAnalysis(false); }, [isLlmSlug, loadAnalysis]);

  const doApprove = async () => {
    if (!sheet || !token) return;
    setActionBusy(true);
    try {
      const prop = await api.proposeAction(token, {
        type: sheet.insight.action_engine.type,
        payload: sheet.editable,
        source_insight_id: sheet.insight.id,
        manager_slug: s,
      });
      const approvedResp = await api.approveAction(token, prop.action_id);
      setApproved({ id: prop.action_id, kind: approvedResp?.result?.kind || sheet.insight.action_engine.type });
      const isRestock = sheet.insight.action_engine.type === 'restock_po';
      setToast({ msg: isRestock ? 'Reorder draft created' : 'Action approved', type: 'success' });
      setTimeout(() => setToast(null), 5000);
      setSheet(null);
    } catch (e) {
      setToast({ msg: e?.message || 'Action failed', type: 'error' });
      setTimeout(() => setToast(null), 5000);
    } finally { setActionBusy(false); }
  };

  // Upsell for non-premium
  if (!isPremium) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid={`manager-upsell-${s}`}>
        <button onClick={() => navigate(-1)} data-testid="manager-back-btn" className="mb-6 w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div className="text-center mt-12">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-brand-purple to-brand-purple-deep flex items-center justify-center mb-6 shadow-accent">
            <Lock size={36} className="text-white" />
          </div>
          <h2 className="text-white font-extrabold text-2xl" data-testid="upsell-title">AI Manager is a Premium feature</h2>
          <p className="text-white/50 text-sm mt-3 max-w-md mx-auto leading-relaxed">
            Unlock strategic insights from {meta.title} and 8 other AI Managers — proactive alerts, deep analysis, and 30-day trend detection across your business.
          </p>
          <button data-testid="manager-upsell-upgrade-btn" className="mt-6 h-13 px-8 rounded-xl bg-gradient-to-r from-brand-purple via-brand-purple-soft to-purple-700 text-white font-bold shadow-accent hover:opacity-90 transition-opacity">
            Start 14-day Free Trial
          </button>
        </div>
      </div>
    );
  }

  const kpi = health?.kpi ?? {};

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid={`manager-screen-${s}`}>
      {/* Header */}
      <button onClick={() => navigate(-1)} data-testid="manager-back-btn" className="mb-4 w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors">
        <ArrowLeft size={18} />
      </button>
      <div
        className="glass-sheen relative rounded-3xl overflow-hidden p-6 mb-6 shadow-clay-dark"
        style={{ background: `linear-gradient(135deg, ${meta.grad[0]}, ${meta.grad[1]})` }}
      >
        <div className="relative z-[2]">
          <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/25 flex items-center justify-center mb-3">
            <meta.Icon size={24} className="text-white" />
          </div>
          <h2 className="text-white font-extrabold text-2xl">{meta.title}</h2>
          <p className="text-white/75 text-sm mt-1">{meta.sub}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><div className="w-6 h-6 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" /></div>
      ) : isLlmSlug ? (
        <>
          <SliceCBody
            analysis={analysis}
            loading={analysisLoading}
            onRefresh={() => loadAnalysis(true)}
            onTakeAction={(insight) => setSheet({ insight, editable: { ...(insight.action_engine?.payload || {}) } })}
            approved={approved}
          />
          {s === 'purchase' && analysis && (
            <PurchaseNegotiatorSections
              stockouts={analysis.stockout_predictions || []}
              smartOrders={analysis.seasonal_smart_orders || []}
              vendorComparison={analysis.internal_vendor_comparison || []}
              approved={approved}
              onReorder={(o) => {
                const vendor = o.cheapest_vendor || {};
                setSheet({
                  insight: {
                    id: `smart-${o.product_id}`,
                    title: `Reorder ${o.product_name}`,
                    action_engine: {
                      type: 'restock_po',
                      payload: {
                        product_id: o.product_id,
                        product_name: o.product_name,
                        quantity: o.suggested_qty,
                        supplier_hint: vendor.vendor_name || '',
                        cost_per_unit: vendor.unit_cost_rupees || o.cost_per_unit || 0,
                        source: 'purchase_manager',
                      },
                    },
                  },
                  editable: {
                    product_id: o.product_id,
                    product_name: o.product_name,
                    quantity: o.suggested_qty,
                    supplier_hint: vendor.vendor_name || '',
                    cost_per_unit: vendor.unit_cost_rupees || o.cost_per_unit || 0,
                  },
                });
              }}
            />
          )}
        </>
      ) : (
        <HealthBody slug={s} kpi={kpi} health={health} />
      )}

      {/* Action Sheet */}
      <AnimatePresence>
        {sheet && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
            data-testid="action-preview-sheet"
          >
            <div className="absolute inset-0" onClick={() => setSheet(null)} />
            <motion.div
              initial={{ y: 300 }}
              animate={{ y: 0 }}
              exit={{ y: 300 }}
              className="relative bg-white rounded-t-3xl w-full max-w-lg p-6"
            >
              <div className="w-11 h-1.5 rounded-full bg-gray-200 mx-auto mb-4" />
              <h3 className="text-gray-900 font-extrabold text-lg">Approve · {String(sheet.insight.action_engine.type).replace(/_/g, ' ')}</h3>
              <p className="text-gray-500 text-sm mt-2">{sheet.insight.title}</p>
              <div className="mt-4 space-y-3">
                {Object.entries(sheet.editable || {}).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">{k.replace(/_/g, ' ')}</p>
                    <p className="text-gray-900 font-semibold mt-1">{String(v ?? '')}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setSheet(null)} data-testid="sheet-dismiss" className="flex-1 h-12 rounded-xl border border-gray-200 text-gray-500 font-bold hover:bg-gray-50 transition-colors">Dismiss</button>
                <button onClick={doApprove} disabled={actionBusy} data-testid="sheet-approve" className="flex-1 h-12 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50">
                  {actionBusy ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /> : 'Approve'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast — plain CSS, outside AnimatePresence to avoid motion conflicts */}
      {toast && (
        <div
          key={toast.msg}
          data-testid="manager-toast"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] px-5 py-3 rounded-2xl text-sm font-semibold shadow-lg flex items-center gap-2 animate-slide-up ${
            toast.type === 'success'
              ? 'bg-emerald-500 text-white'
              : 'bg-red-500/90 text-white'
          }`}
        >
          {toast.type === 'error' && <AlertTriangle size={14} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function HealthBody({ slug, kpi, health }) {
  const revenue = kpi?.revenue_this_month ?? 0;
  const revenueLast = kpi?.revenue_last_month ?? 0;
  const growth = revenueLast > 0 ? ((revenue - revenueLast) / revenueLast * 100) : 0;
  const expenses = kpi?.expenses_this_month ?? 0;
  const grossMargin = revenue > 0 ? ((revenue - expenses) / revenue * 100) : 0;
  const gstPayable = kpi?.gst_payable ?? 0;
  const overdue = kpi?.overdue_invoices_count ?? 0;
  const lowStock = kpi?.low_stock_count ?? 0;
  const outOfStock = kpi?.out_of_stock_count ?? 0;
  const totalSkus = kpi?.total_skus ?? 0;
  const topCustomers = Array.isArray(kpi?.top_customers) ? kpi.top_customers : [];
  const predictedStockouts = Array.isArray(health?.reorder_today) ? health.reorder_today : [];
  // Phase 25.I.3 — ERP-connected tenants: null = the feed doesn't send it.
  const erp = health?.kpi_source === 'bakemill_erp';
  const naExp = erp && kpi?.expenses_this_month == null;
  const NA = 'Not available from ERP';
  const growthText = erp && kpi?.revenue_last_month == null ? NA : `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;

  if (slug === 'ceo') return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <KpiCard label="Revenue (mo)" value={inr(revenue)} />
        <KpiCard label="Growth vs LM" value={growthText} />
        <KpiCard label="Open Fraud" value={String(health?.fraud_alerts_summary?.open_count ?? 0)} />
        <KpiCard label="Open Market" value={String(health?.market_alerts_summary?.open_count ?? 0)} />
      </div>
    </div>
  );
  if (slug === 'cfo') return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <KpiCard label="Revenue (mo)" value={inr(revenue)} />
        <KpiCard label="Expenses (mo)" value={naExp ? NA : inr(expenses)} />
        <KpiCard label="Gross Margin" value={naExp ? NA : `${grossMargin.toFixed(1)}%`} />
        <KpiCard label="GST Payable" value={erp && kpi?.gst_payable == null ? NA : inr(gstPayable)} />
      </div>
      <GlassCard dark className="p-4">
        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-2">Cash flow signal</p>
        <p className="text-white text-sm">Net cash this month: {naExp ? NA : inr(revenue - expenses)}</p>
        {overdue > 0 && <p className="text-white text-sm mt-1">{overdue} overdue invoice{overdue === 1 ? '' : 's'} awaiting payment</p>}
      </GlassCard>
    </div>
  );
  if (slug === 'sales') return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <KpiCard label="Sales (mo)" value={inr(revenue)} />
        <KpiCard label="Overdue" value={String(overdue)} />
      </div>
      <GlassCard dark className="p-4">
        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-2">Top 5 customers</p>
        {topCustomers.length > 0 ? topCustomers.slice(0, 5).map((c, i) => (
          <p key={i} className="text-white text-sm py-1">{c.name ?? 'Customer'} · {inr(c.revenue ?? 0)}</p>
        )) : <p className="text-white/40 text-sm italic">No customer revenue yet this month.</p>}
      </GlassCard>
    </div>
  );
  if (slug === 'inventory') return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <KpiCard label="Total SKUs" value={String(totalSkus)} />
        <KpiCard label="Low stock" value={String(lowStock)} />
        <KpiCard label="Out of stock" value={String(outOfStock)} />
        <KpiCard label="Reorder today" value={String(predictedStockouts.length)} />
      </div>
      <GlassCard dark className="p-4">
        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-2">Next predicted stockouts</p>
        {predictedStockouts.length > 0 ? predictedStockouts.slice(0, 5).map((p, i) => (
          <p key={i} className="text-white text-sm py-1">{p.product_name ?? p.name} · ~{p.days_until_stockout ?? '?'} days</p>
        )) : <p className="text-white/40 text-sm italic">Stock is healthy. Nothing to reorder today.</p>}
      </GlassCard>
    </div>
  );
  // Fraud & Market — show simple alerts
  return (
    <div className="space-y-4">
      <GlassCard dark className="p-4">
        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-2">Alert Summary</p>
        <p className="text-white text-sm">Fraud alerts: {health?.fraud_alerts_summary?.open_count ?? 0} open</p>
        <p className="text-white text-sm mt-1">Market alerts: {health?.market_alerts_summary?.open_count ?? 0} open</p>
      </GlassCard>
    </div>
  );
}

function SliceCBody({ analysis, loading, onRefresh, onTakeAction, approved }) {
  const insights = Array.isArray(analysis?.insights) ? analysis.insights : [];
  const headline = analysis?.headline || '';
  const source = analysis?.source || (analysis?.cached ? 'cache' : '');
  const degraded = analysis?.degraded === true;

  // Compute relative time on every render — no memoization so it always
  // reflects the CURRENT analysis.generated_at value at render time.
  let rel = '';
  if (analysis?.generated_at) {
    try {
      const ms = Date.now() - new Date(analysis.generated_at).getTime();
      const mins = Math.max(0, Math.round(ms / 60000));
      if (mins < 1) rel = 'just now';
      else if (mins < 60) rel = `${mins} min${mins === 1 ? '' : 's'} ago`;
      else rel = `${Math.round(mins / 60)} hr${Math.round(mins / 60) === 1 ? '' : 's'} ago`;
      console.log('[SliceCBody] render — generated_at:', analysis.generated_at, 'rel:', rel);
    } catch (e) { /* ignore */ }
  }

  return (
    <div className="space-y-4">
      {/* Analysis header */}
      <GlassCard dark className="p-4 flex items-center gap-3" testId="analysis-header">
        <div className="flex-1">
          <p className="text-white font-bold text-sm">AI-generated analysis</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {rel && <span className="text-white/40 text-xs" data-testid="analysis-generated-at">Generated {rel}</span>}
            {source && <span className="px-1.5 py-0.5 rounded bg-white/8 text-white/50 text-[10px] font-bold uppercase tracking-wider" data-testid="analysis-source">{source}</span>}
            {degraded && <span className="px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400 text-[10px] font-bold uppercase tracking-wider" data-testid="analysis-degraded">fallback</span>}
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          data-testid="analysis-refresh-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-full bg-white text-navy-900 text-xs font-bold hover:bg-white/90 transition-colors disabled:opacity-50"
        >
          {loading ? <div className="w-3 h-3 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" /> : <RefreshCcw size={12} />}
          Refresh
        </button>
      </GlassCard>

      {headline && (
        <div className="rounded-2xl bg-navy-900 p-4" data-testid="slice-c-headline">
          <p className="text-white font-extrabold text-sm leading-relaxed">{headline}</p>
        </div>
      )}

      {approved && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="slice-c-approved">
          <p className="text-emerald-600 font-bold text-sm">Action created</p>
          <p className="text-emerald-500/80 text-xs mt-1">{approved.kind.replace(/_/g, ' ')} · id {approved.id.slice(0, 8)}</p>
        </div>
      )}

      {/* Insight cards */}
      {loading && insights.length === 0 ? (
        <div className="py-8 flex justify-center"><div className="w-5 h-5 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" /></div>
      ) : insights.length > 0 ? (
        insights.map((it, i) => {
          const sev = SEV_STYLES[it.severity] || SEV_STYLES.info;
          const showAction = it?.action_engine?.type && it.action_engine.type !== 'none';
          return (
            <GlassCard dark key={it.id || i} className="p-4" testId={`insight-card-${it.id || 'x'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${sev.bg} ${sev.text}`}>
                  {String(it.severity || 'info').toUpperCase()}
                </span>
                {it.category && (
                  <span className="px-2 py-0.5 rounded-full bg-brand-purple/15 text-brand-purple-deep text-[10px] font-extrabold uppercase tracking-wider">
                    {String(it.category).toUpperCase()}
                  </span>
                )}
              </div>
              <p className="text-white font-extrabold text-sm">{it.title}</p>
              {it.why && <p className="text-white/70 text-sm mt-1">{it.why}</p>}
              {it.action && <p className="text-white/70 text-sm mt-1">→ {it.action}</p>}
              {it.impact_estimate && (
                <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-white/8 text-white/70 text-xs font-bold">
                  Impact · {it.impact_estimate}
                </span>
              )}
              {showAction && (
                <button
                  onClick={() => onTakeAction(it)}
                  data-testid={`take-action-${it.id || 'x'}`}
                  className="mt-3 px-4 py-2.5 rounded-full bg-white text-navy-900 text-xs font-bold hover:bg-white/90 transition-colors"
                >
                  Take Action · {String(it.action_engine.type).replace(/_/g, ' ')}
                </button>
              )}
            </GlassCard>
          );
        })
      ) : (
        <p className="text-white/40 text-sm italic py-4">No insights right now. Refresh in a bit.</p>
      )}
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <GlassCard dark className="p-4">
      <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{label}</p>
      <p className="text-white font-extrabold text-xl mt-1 tabular-nums">{value}</p>
    </GlassCard>
  );
}
